import React, { useState } from 'react'
import classnames from 'classnames'

import Icon from '@components/Icon'
import Button from '@components/Button'
import Textfield from '@components/Textfield'

import styles from './styles.module.scss'
import { withTranslate } from '@i18n/utils'

const Tab = ({ children, onRemove, onClick, disabled }) => {
    const handleClick = disabled ? undefined : onClick
    const className = classnames(styles.tab, { [styles['tab--disabled']]: disabled })

    return (
        <button onClick={handleClick} className={className}>
            <span className="mr-2">{children}</span>
            {onRemove && (
                <span onClick={onRemove}>
                    <Icon className={styles.close} type="close" size="xxs" />
                </span>
            )}
        </button>
    )
}

export const VehicleTabs = compose(withTranslate)(({
    userVehicles,
    vehicles = [],
    setSelectedVehicles,
    maxVehicles = Infinity,
    isVehiclesListOpen = false,
    canAddPlate = true,
    translate: t,
    disabled,
}) => {
    const [newPlateInput, setNewPlateInput] = useState(null)
    const [addPlateOpen, setAddPlateOpen] = useState(false)
    const [showVehiclesList, setShowVehiclesList] = useState(false)

    const isFull = vehicles.length >= maxVehicles

    const setUserVehicle = vehicle => {
        const { id, tag } = vehicle
        const isAlreadyOnList = vehicles.find(({ id: $id }) => $id === id)
        if (!isFull && !isAlreadyOnList) {
            setSelectedVehicles([...vehicles, { id, tag }])
        }
    }

    const removeVehicle = vehicle => {
        setSelectedVehicles(vehicles.filter(v => v.tag !== vehicle.tag))
    }

    const showAddPlate = () => {
        setAddPlateOpen(true)

        if (showVehiclesList) {
            setShowVehiclesList(false)
        }
    }

    const saveNewTag = () => {
        if (newPlateInput) {
            setUserVehicle({ tag: newPlateInput })
            setNewPlateInput(null)
            setAddPlateOpen(false)
        }
    }

    const closeTagInput = () => {
        setAddPlateOpen(false)

        if (newPlateInput) {
            setNewPlateInput(null)
        }
    }

    return (
        <>
            <div className={styles.vehicleTabs}>
                {vehicles.map(veh => (
                    <Tab onRemove={!disabled && (() => removeVehicle(veh))} key={veh.id}>
                        {veh.tag}
                    </Tab>
                ))}
            </div>
            {!addPlateOpen && (
                <div className="d-md-flex">
                    {canAddPlate && (
                        <Button
                            btnStyle="outline"
                            type="button"
                            className={classnames('mr-3 mb-3', styles.addPlateBtn)}
                            onClick={showAddPlate}
                            disabled={disabled}>
                            <T>dashboard.subscriptions.assign.parker.label.add.plate</T>
                        </Button>
                    )}
                    {!isVehiclesListOpen && (
                        <>
                            {showVehiclesList ? (
                                <Button
                                    type="button"
                                    onClick={() => setShowVehiclesList(false)}
                                    btnStyle="link"
                                    className="mt-3 mt-md-0">
                                    <T>dashboard.subscriptions.assign.parker.label.vehicles.list.close</T>
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    btnStyle="link"
                                    onClick={() => setShowVehiclesList(true)}
                                    className="mt-sm-3 mt-md-0"
                                    disabled={disabled}>
                                    <T>dashboard.subscriptions.assign.parker.label.vehicles.list</T>
                                </Button>
                            )}
                        </>
                    )}
                </div>
            )}
            {(isVehiclesListOpen || showVehiclesList) && (
                <div className={styles.userVehicleTabs}>
                    {userVehicles
                        .filter(v => !vehicles.includes(v.tag))
                        .map((v, i) => (
                            <Tab onClick={() => setUserVehicle(v)} key={i} disabled={isFull}>
                                {v.tag}
                            </Tab>
                        ))}
                </div>
            )}
            {addPlateOpen && (
                <div className={classnames('d-flex', 'align-items-center', 'mt-3')}>
                    <Textfield
                        className={styles.addVehicleInput}
                        label={t('dashboard.subscriptions.assign.parker.placeholder.add.plate')}
                        onChange={e => setNewPlateInput(e?.target?.value)}
                        required
                    />
                    <Button type="button" className="ml-2" btnStyle="primary" onClick={saveNewTag}>
                        <T>dashboard.account.save</T>
                    </Button>
                    <Button type="button" btnStyle="link" onClick={closeTagInput}>
                        <T>dashboard.label.cancel</T>
                    </Button>
                </div>
            )}
        </>
    )
})
